import __snakeCase from 'lodash/snakeCase'
import Qs from 'qs'
import type { Dispatch } from 'redux'

import type { AuthToken } from '@libs/typescript-utils'
import { APP_NAME, checkTokenExpirationStatus, clearAuthToken } from '@libs/typescript-utils'

import actions from './auth/actions'

export function checkTokenExpiration(dispatch: Dispatch, token: AuthToken) {
  // this just all works to compare the total seconds of the created
  // time of the token vs the EXPIRATION_RENEWAL_TIME seconds
  // check and call action to update token when needed
  const tokenAlive = checkTokenExpirationStatus(token)
  // token is not expired: tokenAlive is equal with 1 or 2
  if (tokenAlive > 0) {
    // renew token when nearly expired
    if (tokenAlive === 1) {
      dispatch(actions.renewToken.request())
    }
  } else {
    // expired token
    // need to clear token before dispatch to avoid infinite state update loop
    clearAuthToken(APP_NAME.TMS)
    // notify reducer to display expired warning dialog in InitializeApplication
    dispatch(actions.expiredToken.request())
  }
}

/**
 * Helper function to transform object with camelCase keys to object with snake_case keys (non recursive)
 * @param {object} params - object with camelCase keys
 * @returns object with snake_case keys (only 1 level deep)
 */
export function transformV2ParamsToV1(params: object): object {
  return Object.fromEntries(Object.entries(params).map(([key, value]) => [__snakeCase(key), value]))
}

export function serializeParamsWithCustomFields({ ...params }: Record<string, unknown>) {
  return Qs.stringify(params)
}

/**
 * Helper function to create a map of paging list items
 * @param list - list of items
 * @param prevLastId - previous last id (optional)
 * @returns - map of paging list items
 */
function createPagingListMap<Data extends { id: ItemIdType }>(list: Data[], prevLastId: Nullable<ItemIdType> = null) {
  const map = {} as Record<string, DetailsPagingMapItem>
  let lastId = prevLastId

  list.forEach((item, index) => {
    const id = item.id

    map[id] = {
      id,
      prevItemId: lastId,
      nextItemId: index < list.length - 1 ? list[index + 1].id : null,
      position: index + 1,
    }

    lastId = id
  })

  return map
}

export function createDetailsPagingConfig<Data extends { id: ItemIdType }>(
  detailsPageRouteKey: DetailsPagingConfigProps['detailsPageRouteKey'],
  data: Data[]
): DetailsPagingConfigProps {
  return {
    detailsPageRouteKey,
    itemsCount: data.length,
    listMap: createPagingListMap(data),
  }
}
